import { InputAdornment, Typography } from '@mui/material';
import { compact, isEmpty } from 'lodash';
import React, { ReactElement, useContext, useEffect } from 'react';
import { useWatch } from 'react-hook-form';

import { FormAwareCheckbox } from '@/components/form/formAwareInputs/FormAwareCheckbox';
import { FormAwareDatePickerInput } from '@/components/form/formAwareInputs/FormAwareDatePickerInput';
import { FormAwareSelectInput } from '@/components/form/formAwareInputs/FormAwareSelectInput';
import { FormAwareSwitch } from '@/components/form/formAwareInputs/FormAwareSwitch';
import { FormAwareTextInput } from '@/components/form/formAwareInputs/FormAwareTextInput';
import {
  GRANTOR_RELATIONSHIP_OPTIONS,
  PRIMARY_CLIENT_RELATIONSHIP_OPTIONS,
} from '@/components/form/PersonFormFragment/constants';
import { Mail01Icon } from '@/components/icons/Mail01Icon';
import { UsersLeftIcon } from '@/components/icons/UsersLeftIcon';
import { Card } from '@/components/layout/Card/Card';
import { FormLayoutItem, FormLayoutRow } from '@/components/layout/FormLayout';
import { PopperContent } from '@/components/poppers/PopperContent';
import { useFormContext } from '@/components/react-hook-form';
import {
  LEGAL_NAME_LABELS,
  PREFERRED_NAME_HELP_BODY,
  PREFERRED_NAME_LABEL,
} from '@/modules/clientProfiles/clientProfile.constants';
import { ClientProfileFormShape } from '@/modules/clientProfiles/ClientProfile.types';
import { StateDropdownInput } from '@/modules/common/StateDropdownInput/StateDropdownInput';
import { useFeatureFlag } from '@/modules/featureFlags/useFeatureFlag';
import { useHouseholdDetailsContext } from '@/modules/household/contexts/householdDetails.context';
import {
  ClientProfileDisplayNamePattern,
  ClientProfileRelationshipType,
} from '@/types/schema';
import { PathsOf } from '@/types/subform';

import { ClientProfileSideDrawerContext } from './ClientProfileModal.context';

function getRelationshipPath(
  fieldName: number
): `relationships.${number}.type` {
  return `relationships.${fieldName}.type` satisfies PathsOf<ClientProfileFormShape>;
}

function formatDisplayName(parts: Array<string | undefined>): string {
  return compact(parts.map((part) => part?.trim())).join(' ');
}

// These options preview the display name only. The backend defines the core
// display-name formatting logic used throughout the application.
export function getDisplayNamePatternOptions({
  firstName,
  preferredName,
  middleName,
  lastName,
  suffix,
}: Pick<
  ClientProfileFormShape,
  'firstName' | 'preferredName' | 'middleName' | 'lastName' | 'suffix'
>) {
  const givenName = preferredName.trim() || firstName.trim();
  const middleInitial = middleName.trim()
    ? `${Array.from(middleName.trim())[0]}.`
    : undefined;

  return [
    {
      value: ClientProfileDisplayNamePattern.FirstLastSuffix,
      display: formatDisplayName([givenName, lastName, suffix]),
      secondaryText: 'Preferred or first name, last name, and suffix',
    },
    {
      value: ClientProfileDisplayNamePattern.FirstMiLastSuffix,
      display: formatDisplayName([givenName, middleInitial, lastName, suffix]),
      secondaryText:
        'Preferred or first name, middle initial, last name, and suffix',
    },
    {
      value: ClientProfileDisplayNamePattern.FirstMiddleLastSuffix,
      display: formatDisplayName([givenName, middleName, lastName, suffix]),
      secondaryText:
        'Preferred or first name, full middle name, last name, and suffix',
    },
    {
      value: ClientProfileDisplayNamePattern.FirstLast,
      display: formatDisplayName([givenName, lastName]),
      secondaryText: 'Preferred or first name and last name',
    },
  ];
}

interface GrantorRelationshipsProps {
  hideRelationships?: boolean;
}

function GrantorRelationships({
  hideRelationships = false,
}: GrantorRelationshipsProps) {
  const { control, setValue } = useFormContext<ClientProfileFormShape>();
  const { primaryClients } = useHouseholdDetailsContext();
  const { isPrimary: isCurrentlyPrimaryClient } = useContext(
    ClientProfileSideDrawerContext
  );
  const [relationships, clientId, isPrimary] = useWatch({
    name: ['relationships', 'id', 'isPrimary'],
    control,
  });

  const primaryClientIds: string[] = compact(primaryClients?.map((c) => c.id));

  // Only show the "Add as additional client" toggle for single primary client households
  const hasSinglePrimaryClient = primaryClients?.length === 1;
  const primaryClient = hasSinglePrimaryClient ? primaryClients[0] : null;

  // Valid relationship types that allow setting as additional client
  const validRelationshipTypes = React.useMemo(
    () => [
      ClientProfileRelationshipType.Spouse,
      ClientProfileRelationshipType.DomesticPartner,
      ClientProfileRelationshipType.ExSpouse,
    ],
    []
  );

  // Automatically turn off isPrimary when relationship changes to an invalid type
  const relationshipToPrimaryClient = relationships?.find(
    (rel) => rel.toClientProfileId === primaryClient?.id
  );

  useEffect(() => {
    if (
      isPrimary &&
      relationshipToPrimaryClient &&
      !validRelationshipTypes.includes(
        relationshipToPrimaryClient.type as ClientProfileRelationshipType
      )
    ) {
      setValue('isPrimary', false);
    }
  }, [
    isPrimary,
    relationshipToPrimaryClient,
    setValue,
    validRelationshipTypes,
  ]);

  // Check if the person is marked with a valid relationship to the primary client
  const hasValidRelationshipToPrimaryClient =
    hasSinglePrimaryClient &&
    relationships?.some(
      (rel) =>
        rel.toClientProfileId === primaryClient?.id &&
        validRelationshipTypes.includes(
          rel.type as ClientProfileRelationshipType
        )
    );

  if (hideRelationships || isEmpty(relationships)) {
    return null;
  }

  return (
    <FormLayoutRow>
      <FormLayoutItem>
        <Card variant="filled" sx={{ p: 2 }}>
          <Typography variant="title-s" sx={{ pb: 1 }}>
            Relationship to client(s)
          </Typography>
          <FormLayoutRow>
            {relationships.map((relationship, index) => {
              const isPrimaryClientRelationship = Boolean(
                clientId &&
                  relationship.toClientProfileId &&
                  primaryClientIds?.includes(clientId) &&
                  primaryClientIds.includes(relationship.toClientProfileId)
              );
              return (
                <FormLayoutItem
                  width={6}
                  key={`relationship-${relationship.toClientProfileId}`}
                >
                  <FormAwareSelectInput<ClientProfileFormShape>
                    fieldName={getRelationshipPath(index)}
                    label={relationship.display}
                    required={isPrimaryClientRelationship}
                    options={
                      isPrimaryClientRelationship
                        ? PRIMARY_CLIENT_RELATIONSHIP_OPTIONS
                        : GRANTOR_RELATIONSHIP_OPTIONS
                    }
                    control={control}
                    startAdornment={
                      <InputAdornment position="start">
                        <UsersLeftIcon size={16} />
                      </InputAdornment>
                    }
                  />
                </FormLayoutItem>
              );
            })}
          </FormLayoutRow>

          {hasSinglePrimaryClient &&
            !isCurrentlyPrimaryClient &&
            hasValidRelationshipToPrimaryClient && (
              <FormLayoutRow>
                <FormLayoutItem>
                  <FormAwareSwitch<ClientProfileFormShape>
                    control={control}
                    label="Add as additional client"
                    fieldName="isPrimary"
                    labelPosition="right"
                    helpText={
                      primaryClient
                        ? `${primaryClient.displayName} will become a dual-grantor client`
                        : undefined
                    }
                  />
                </FormLayoutItem>
              </FormLayoutRow>
            )}
        </Card>
      </FormLayoutItem>
    </FormLayoutRow>
  );
}
export interface ClientProfilePersonProps {
  disableGrantor?: boolean;
  hideRelationships?: boolean;
}

export function ClientProfilePerson({
  disableGrantor = false,
  hideRelationships = false,
}: ClientProfilePersonProps): ReactElement {
  const { control } = useFormContext<ClientProfileFormShape>();
  const { isPrimary } = useContext(ClientProfileSideDrawerContext);
  const focusInsightsWave1 = useFeatureFlag('focus_insights_wave_1');

  // if the profile is for a grantor, or if either city or state are present, then both city & state are required
  const hasCity = !!useWatch({
    control,
    name: 'address.cityName',
  });

  const residesOutsideUnitedStates = useWatch({
    control,
    name: 'address.residesOutsideUnitedStates',
  });

  const isDeceased = useWatch({
    control,
    name: 'isDeceased',
  });

  const [firstName, preferredName, middleName, lastName, suffix] = useWatch({
    control,
    name: ['firstName', 'preferredName', 'middleName', 'lastName', 'suffix'],
  });
  const displayNamePatternOptions = getDisplayNamePatternOptions({
    firstName,
    preferredName,
    middleName,
    lastName,
    suffix,
  });

  return (
    <>
      <FormLayoutRow>
        <FormLayoutItem width={6}>
          <FormAwareTextInput<ClientProfileFormShape>
            fieldName={'firstName'}
            label={LEGAL_NAME_LABELS.firstName}
            control={control}
            required
          />
        </FormLayoutItem>
        <FormLayoutItem width={6}>
          <FormAwareTextInput<ClientProfileFormShape>
            fieldName={'middleName'}
            label={LEGAL_NAME_LABELS.middleName}
            placeholder="Optional"
            control={control}
          />
        </FormLayoutItem>
      </FormLayoutRow>

      <FormLayoutRow>
        <FormLayoutItem width={10}>
          <FormAwareTextInput<ClientProfileFormShape>
            control={control}
            label={LEGAL_NAME_LABELS.lastName}
            required
            fieldName="lastName"
          />
        </FormLayoutItem>
        <FormLayoutItem width={2}>
          <FormAwareTextInput<ClientProfileFormShape>
            control={control}
            label="Suffix"
            fieldName="suffix"
          />
        </FormLayoutItem>
      </FormLayoutRow>

      <FormLayoutRow>
        <FormLayoutItem width={6}>
          <FormAwareTextInput<ClientProfileFormShape>
            control={control}
            label={PREFERRED_NAME_LABEL}
            placeholder="Optional"
            contextualHelp={<PopperContent body={PREFERRED_NAME_HELP_BODY} />}
            fieldName="preferredName"
          />
        </FormLayoutItem>
        <FormLayoutItem width={6}>
          <FormAwareSelectInput<ClientProfileFormShape>
            control={control}
            label="Display name format"
            fieldName="displayNamePattern"
            options={displayNamePatternOptions}
            required
            showEmptyValue={false}
          />
        </FormLayoutItem>
      </FormLayoutRow>

      <FormLayoutRow>
        <FormLayoutItem width={8}>
          <FormAwareTextInput<ClientProfileFormShape>
            control={control}
            label="City"
            fieldName="address.cityName"
          />
        </FormLayoutItem>
        <FormLayoutItem width={4}>
          <StateDropdownInput<ClientProfileFormShape>
            label="State"
            required={
              isPrimary ||
              (!residesOutsideUnitedStates &&
                hasCity) /* if city is entered, state is required.  checking this way so user can still clear the state. */
            }
            fieldName="address.stateCode"
            control={control}
          />
        </FormLayoutItem>
      </FormLayoutRow>
      {focusInsightsWave1 && !isPrimary && (
        <FormLayoutRow>
          <FormLayoutItem>
            <FormAwareCheckbox<ClientProfileFormShape>
              control={control}
              label="Lives outside of the United States"
              fieldName="address.residesOutsideUnitedStates"
            />
          </FormLayoutItem>
        </FormLayoutRow>
      )}
      <FormLayoutRow>
        <FormLayoutItem>
          <FormAwareTextInput<ClientProfileFormShape>
            control={control}
            label="Email"
            type="email"
            fieldName="email"
            startAdornment={
              <InputAdornment position="start">
                <Mail01Icon size={16} />
              </InputAdornment>
            }
          />
        </FormLayoutItem>
      </FormLayoutRow>
      <FormLayoutRow>
        <FormLayoutItem>
          <FormAwareTextInput<ClientProfileFormShape>
            label="Notes"
            multiline
            fieldName="notes"
            control={control}
            rows={2}
            resizable
          />
        </FormLayoutItem>
      </FormLayoutRow>
      <FormLayoutRow>
        <FormLayoutItem width={6}>
          <FormAwareDatePickerInput
            control={control}
            label="Date of birth"
            fieldName="dateOfBirth"
            omitCapture
          />
        </FormLayoutItem>
        {isDeceased && (
          <FormLayoutItem width={6}>
            <FormAwareDatePickerInput
              control={control}
              label="Date of death"
              fieldName="dateOfDeath"
            />
          </FormLayoutItem>
        )}
        <FormLayoutItem sx={{ alignSelf: 'end', pb: 1 }}>
          <FormAwareSwitch<ClientProfileFormShape>
            control={control}
            label="Deceased"
            fieldName="isDeceased"
            labelPosition="right"
          />
        </FormLayoutItem>
      </FormLayoutRow>

      <GrantorRelationships hideRelationships={hideRelationships} />

      <FormLayoutRow>
        <FormLayoutItem>
          <FormAwareCheckbox<ClientProfileFormShape>
            disabled={disableGrantor}
            control={control}
            label="Enable use as a grantor/owner/donor"
            fieldName="isGrantor"
            helpText={
              isPrimary
                ? 'Clients are enabled as grantors by default. This behavior cannot be changed'
                : undefined
            }
          />
        </FormLayoutItem>
      </FormLayoutRow>
    </>
  );
}
